import { Person, LocalShipping, CheckCircle, PhotoCamera, PhotoAlbumOutlined } from '@mui/icons-material'
import { Box, Card, CardContent, Grid, Typography } from '@mui/material'
import { useMemo } from 'react'

import { UserPhotoUploadSummary } from 'hooks/admin/usePhotoUploads'

interface PhotoUploadOverviewProps {
  photoUploads: UserPhotoUploadSummary[]
  isLoading: boolean
}

const OverviewCard = ({ title, value, subtitle, icon, color = 'primary' }: {
  title: string
  value: string | number
  subtitle?: string
  icon: React.ReactNode
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error'
}) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ pb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ 
          p: 1, 
          borderRadius: 2, 
          backgroundColor: `${color}.light`,
          color: `${color}.contrastText`,
          display: 'flex',
          alignItems: 'center'
        }}>
          {icon}
        </Box>
      </Box>
      <Typography variant='h4' component='div' sx={{ fontWeight: 600, mb: 0.5 }}>
        {value}
      </Typography>
      <Typography variant='body2' color='text.primary' sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant='caption' color='text.secondary'>
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
)

export const PhotoUploadOverview = ({ photoUploads, isLoading }: PhotoUploadOverviewProps) => {
  // Calculate overview statistics
  const overviewStats = useMemo(() => {
    const totalPeople = photoUploads.length
    const totalTrucks = photoUploads.reduce((sum, userSummary) => sum + userSummary.totalVehicles, 0)
    
    // Use new photo-based calculations if available, fallback to old method
    const actualPhotosUploaded = photoUploads.reduce((sum, userSummary) => {
      return sum + (userSummary.actualPhotoCount !== undefined ? userSummary.actualPhotoCount : userSummary.submissionCount * 14)
    }, 0)
    
    const expectedPhotosTotal = photoUploads.reduce((sum, userSummary) => {
      return sum + (userSummary.expectedPhotoCount !== undefined ? userSummary.expectedPhotoCount : userSummary.totalVehicles * 14)
    }, 0)
    
    // Calculate completed trucks based on photo completion (14 photos = completed truck)
    const completedTrucks = photoUploads.reduce((sum, userSummary) => {
      if (userSummary.actualPhotoCount !== undefined) {
        // Count trucks that have all 14 photos
        return sum + userSummary.vehicles.filter(vehicle => (vehicle.photoCount || 0) >= 14).length
      } else {
        // Fallback to old method
        return sum + userSummary.submissionCount
      }
    }, 0)
    
    // Calculate total photo upload documents (links sent)
    const totalPhotoUploadDocs = photoUploads.length
    
    return {
      totalPeople,
      totalTrucks,
      completedTrucks,
      totalPhotoUploadDocs,
      photosNeeded: expectedPhotosTotal,
      photosUploaded: actualPhotosUploaded,
      completionRate: totalTrucks > 0 ? Math.round((completedTrucks / totalTrucks) * 100) : 0,
      uploadProgress: expectedPhotosTotal > 0 ? Math.round((actualPhotosUploaded / expectedPhotosTotal) * 100) : 0
    }
  }, [photoUploads])

  if (isLoading) {
    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {Array.from({ length: 5 }).map((_, index) => (
          <Grid item xs={12} sm={6} md={2.4} key={index}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ pb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                  <Typography variant='body2' color='text.secondary'>
                    Loading...
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    )
  }

  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={6} md={2.4}>
        <OverviewCard
          title='Total People'
          value={overviewStats.totalPeople}
          subtitle={`${overviewStats.totalPhotoUploadDocs} links sent`}
          icon={<Person />}
          color='primary'
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2.4}>
        <OverviewCard
          title='Total Trucks'
          value={overviewStats.totalTrucks}
          icon={<LocalShipping />}
          color='secondary'
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2.4}>
        <OverviewCard
          title='Completed Trucks'
          value={overviewStats.completedTrucks}
          subtitle={`${overviewStats.completionRate}% completion rate`}
          icon={<CheckCircle />}
          color='success'
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2.4}>
        <OverviewCard
          title='Photos Needed'
          value={overviewStats.photosNeeded}
          icon={<PhotoCamera />}
          color='warning'
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2.4}>
        <OverviewCard
          title='Photos Uploaded'
          value={overviewStats.photosUploaded}
          subtitle={`${overviewStats.uploadProgress}% of total needed`}
          icon={<PhotoAlbumOutlined />}
          color='success'
        />
      </Grid>
    </Grid>
  )
} 